//@flow
import React, {useMemo, useState} from 'react';
import {makeStyles, Typography, Button, IconButton} from '@material-ui/core';

import {getImageUrl} from '../../../utils/image_provider';
import {commaFormat, percentageRatio} from '../../../utils/number';

import {colors} from '../../../assets/styles/colors';
import {Add, LocalOfferOutlined} from '@material-ui/icons';
import {useIsFeatureInTier} from '../../../utils/component';
import DiscountIcon from './icons/Discount';
import {getProductItemPricing} from '../../../utils/product';

import {useProductOutOfStock} from '../utils';
import BulkPriceModal from './BulkPriceModal';

import type {Product, Currency} from '../../../entities';
import {classNames} from '../../../utils/react';
type Props = {
  outOfStock: boolean,
  product: Product,
  currency: Currency,
  isInPromotion: boolean,
  onAdd: (product: Product) => void,
};

const useStyles = makeStyles(theme => ({
  product: {
    padding: '12px',
    borderRadius: '20px',
    background: colors.grey200,
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'initial',
    },
  },
  productImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  productImage: {
    width: '80px',
    objectFit: 'cover',
    aspectRatio: '1/1',
    borderRadius: '12px',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  productName: {
    fontSize: '14px',
    color: colors.grey900,
  },
  productDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '8px',
    flexGrow: 1,

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  productPrice: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
  },
  productPriceNormal: {
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
  productNoStockFlag: {
    position: 'absolute',
    top: '50%',
    right: '50%',
    transform: 'translate(50%, -50%)',
    backgroundColor: colors.grey200,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    borderRadius: '24px',
    width: 'fit-content',
    padding: '8px 16px',
  },
  disabledProduct: {
    opacity: 0.5,
    cursor: 'not-allowed',
    filter: 'grayscale(50%)',
  },
  productPriceSlashed: {
    fontSize: '12px',
    color: colors.grey500,
    textDecorationLine: 'line-through',
    whiteSpace: 'nowrap',
  },
  discount: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    backgroundColor: colors.purple,
    color: 'white',
    padding: '4px',
    borderRadius: '32px',
    fontSize: '10px',
    [theme.breakpoints.up('md')]: {
      top: '8px',
      left: '8px',
      fontSize: '13px',
    },
  },
  floatContainer: {
    position: 'absolute',
    top: '2px',
    right: '2px',
  },
  floatContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  promotionSticker: {
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '32px',
    color: colors.grey700,
    background: colors.grey200,
    transform: 'scale(0.8)',
    [theme.breakpoints.up('md')]: {
      top: '8px',
      right: '8px',
      transform: 'scale(1)',
    },
  },
  bulkPriceSticker: {
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '32px',
    color: colors.grey700,
    background: colors.grey200,
    transform: 'scale(0.8)',
    [theme.breakpoints.up('md')]: {
      top: '8px',
      right: '8px',
      transform: 'scale(1)',
    },
  },
  productAddButton: {
    display: 'none',
    width: '100%',
    borderRadius: '999px',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'initial',
    },
  },
  productAddButtonIcon: {
    padding: '0px',
    fontSize: '16px',
    background: colors.grey500,
    color: 'white',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

export default function ProductItem(props: Props) {
  const {
    outOfStock: initialOutOfStock,
    product,
    currency,
    isInPromotion,
    onAdd,
  } = props;

  // const hasTaxesFeature = useIsFeatureInTier('TAXES');

  const [isBulkPriceModalOpen, setIsBulkPriceModalOpen] = useState<boolean>(
    false
  );
  const isOutOfStock = useProductOutOfStock(product.id);

  const classes = useStyles();

  const hasPromotionFeature = useIsFeatureInTier('STORE_PROMOTION');

  const productPricing = useMemo(() => getProductItemPricing(product), [
    product,
  ]);

  const hasLowerPrice = !!productPricing.offerPrice;

  const outOfStock = initialOutOfStock || isOutOfStock;

  const bulkPrices = product.bulkPriceSetting?.bulkPrices;

  const productTaxFactor = 1;

  return (
    <div
      className={classNames(classes.product, {
        [classes.disabledProduct]: outOfStock,
      })}
    >
      {bulkPrices?.length && (
        <BulkPriceModal
          bulkPrices={bulkPrices}
          open={isBulkPriceModalOpen}
          handleClose={() => {
            setIsBulkPriceModalOpen(false);
          }}
          taxFactor={productTaxFactor}
        />
      )}

      <picture className={classes.productImageContainer}>
        <img
          className={classes.productImage}
          src={getImageUrl(product.mainImageId)}
          alt=""
        />
        {productPricing.offerPrice && (
          <span className={classes.discount}>
            {percentageRatio(productPricing.offerPrice, productPricing.price)}
          </span>
        )}

        <div className={classes.floatContainer}>
          <div className={classes.floatContent}>
            {hasPromotionFeature && isInPromotion && (
              <div className={classes.promotionSticker}>
                <DiscountIcon size={18} />
              </div>
            )}

            {bulkPrices && bulkPrices.length > 0 && (
              <div
                className={classes.bulkPriceSticker}
                onClick={() => setIsBulkPriceModalOpen(true)}
              >
                <LocalOfferOutlined fontSize="small" />
              </div>
            )}
          </div>
        </div>
      </picture>
      <div className={classes.productDetail}>
        <Typography className={classes.productName}>{product.name}</Typography>

        {outOfStock && (
          <div className={classes.productNoStockFlag}>Sin Stock</div>
        )}
      </div>
      <div className={classes.productPrice}>
        {productPricing.offerPrice && (
          <Typography className={classes.productPriceNormal}>
            {commaFormat(
              parseFloat(productPricing.offerPrice) * productTaxFactor,
              currency?.symbol
            )}
          </Typography>
        )}
        <Typography
          className={
            hasLowerPrice
              ? classes.productPriceSlashed
              : classes.productPriceNormal
          }
        >
          {commaFormat(
            parseFloat(productPricing.price) * productTaxFactor,
            currency?.symbol
          )}
        </Typography>
      </div>
      <IconButton
        color="primary"
        onClick={onAdd}
        disabled={outOfStock}
        className={classes.productAddButtonIcon}
      >
        <Add />
      </IconButton>
      <Button
        color="primary"
        onClick={onAdd}
        disabled={outOfStock}
        className={classes.productAddButton}
        variant="outlined"
      >
        Agregar
      </Button>
    </div>
  );
}
